<template>
  <v-card outlined>
    <v-card-text class="pa-0">
      <v-container class="px-4 pt-4 pb-2">
        <v-layout row wrap align-center>
          <v-flex xs3>
            <v-icon size="36px" color="primary">{{ icon }}</v-icon>
          </v-flex>
          <v-flex xs9 class="text-right">
            <div class="display-1 font-weight-bold primary--text">{{ total }}</div>
            <div class="subheading primary--text">{{ title }}</div>
          </v-flex>
        </v-layout>
      </v-container>

      <!-- Sparkline -->
      <div
        v-if="current && current.length > 1"
        class="sparkline-wrap"
        ref="sparkWrap"
        @mousemove="onHover"
        @mouseleave="tooltip = null"
      >
        <svg :viewBox="`0 0 300 60`" preserveAspectRatio="none" class="sparkline-svg">
          <!-- Previous (grey dashed) -->
          <path v-if="prevArea" :d="prevArea" class="spark-area-prev" />
          <path v-if="prevLine" :d="prevLine" class="spark-line-prev" />
          <!-- Current (accent) -->
          <path v-if="currArea" :d="currArea" class="spark-area-curr" />
          <path v-if="currLine" :d="currLine" class="spark-line-curr" />
          <!-- Hover line -->
          <line
            v-if="tooltip"
            :x1="tooltip.svgX" :x2="tooltip.svgX"
            y1="0" y2="60"
            class="spark-hover-line"
          />
        </svg>
        <!-- Tooltip bubble -->
        <div
          v-if="tooltip"
          class="spark-tooltip"
          :style="tooltipStyle"
        >
          <div class="spark-tooltip-date">{{ tooltip.date }}</div>
          <div class="spark-tooltip-val">{{ tooltip.curr | local_numbers }}</div>
          <div v-if="tooltip.prev !== null" class="spark-tooltip-prev">
            prev: {{ tooltip.prev | local_numbers }}
          </div>
        </div>
      </div>

      <!-- Footer stats -->
      <v-layout row class="text-center pb-2 px-2">
        <v-flex>
          <div class="caption text--secondary">{{ predicted }}</div>
          <div class="overline text--secondary">Expected</div>
        </v-flex>
        <v-flex v-if="conversion !== null">
          <div class="caption text--secondary">{{ conversion }}</div>
          <div class="overline text--secondary">Conversion</div>
        </v-flex>
        <v-flex v-if="roi !== null">
          <div class="caption text--secondary">{{ roi !== 0 ? Number(roi.toFixed(2)) : roi }}</div>
          <div class="overline text--secondary">ROI</div>
        </v-flex>
        <v-flex v-if="cpc !== null">
          <div class="caption text--secondary">{{ cpc !== 0 ? Number(cpc.toFixed(2)) : cpc }}</div>
          <div class="overline text--secondary">CPC</div>
        </v-flex>
      </v-layout>
    </v-card-text>
  </v-card>
</template>

<script>
import local_numbers from "@/helpers/filters/local_numbers";

const W = 300, H = 60;

function pts(arr) { return (arr || []).map(d => Number(d[1])); }
function dates(arr) { return (arr || []).map(d => d[0]); }

function makePath(values, minV, maxV, close) {
  if (!values.length) return "";
  const range = maxV - minV || 1;
  const xs = values.map((_, i) => (i / Math.max(values.length - 1, 1)) * W);
  const ys = values.map(v => H - ((v - minV) / range) * (H * 0.8) - H * 0.1);
  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 1; i < values.length; i++) {
    const cx = (xs[i - 1] + xs[i]) / 2;
    d += ` C ${cx} ${ys[i-1]}, ${cx} ${ys[i]}, ${xs[i]} ${ys[i]}`;
  }
  if (close) d += ` L ${xs[xs.length-1]} ${H} L ${xs[0]} ${H} Z`;
  return d;
}

export default {
  filters: { local_numbers },
  props: {
    icon:       { type: String,          required: true },
    title:      { type: String,          required: true },
    total:      { type: [Number, String],required: true },
    predicted:  { type: [Number, String],required: true },
    conversion: { type: [Number, String],required: false, default: null },
    roi:        { type: [Number, String],required: false, default: null },
    cpc:        { type: [Number, String],required: false, default: null },
    epc:        { type: [Number, String],required: false, default: null },
    current:    { type: Array,           required: true },
    previous:   { type: Array,           required: true },
  },
  data: () => ({ tooltip: null }),
  computed: {
    currValues() { return pts(this.current); },
    prevValues() { return pts(this.previous); },
    currDates()  { return dates(this.current); },
    prevDates()  { return dates(this.previous); },
    globalMin() { return Math.min(0, ...this.currValues, ...this.prevValues); },
    globalMax() { return Math.max(1, ...this.currValues, ...this.prevValues); },
    currLine() { return makePath(this.currValues, this.globalMin, this.globalMax, false); },
    currArea() { return makePath(this.currValues, this.globalMin, this.globalMax, true);  },
    prevLine() { return makePath(this.prevValues, this.globalMin, this.globalMax, false); },
    prevArea() { return makePath(this.prevValues, this.globalMin, this.globalMax, true);  },
    tooltipStyle() {
      if (!this.tooltip) return {};
      const el = this.$refs.sparkWrap;
      const w = el ? el.offsetWidth : 300;
      // flip tooltip to left side when too close to right edge
      const flip = this.tooltip.px > w - 90;
      return {
        left: flip ? 'auto' : this.tooltip.px + 'px',
        right: flip ? (w - this.tooltip.px) + 'px' : 'auto',
        transform: flip ? 'none' : 'translateX(-50%)',
      };
    },
  },
  methods: {
    onHover(e) {
      const el = this.$refs.sparkWrap;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const xPct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const ci = this.currValues.length ? Math.round(xPct * (this.currValues.length - 1)) : -1;
      const pi = this.prevValues.length ? Math.round(xPct * (this.prevValues.length - 1)) : -1;
      const raw = ci >= 0 ? this.currDates[ci] : (pi >= 0 ? this.prevDates[pi] : null);
      this.tooltip = {
        px:   xPct * rect.width,
        svgX: xPct * W,
        date: raw ? this.$moment.parseZone(raw).utc().format("MMM D, YYYY") : "",
        curr: ci >= 0 ? this.currValues[ci] : null,
        prev: pi >= 0 ? this.prevValues[pi] : null,
      };
    },
  },
};
</script>

<style scoped>
.sparkline-wrap {
  position: relative;
  width: 100%;
  height: 70px;
  cursor: crosshair;
}
.sparkline-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.spark-area-curr { fill: var(--v-accent-base, #1976d2); opacity: 0.12; }
.spark-line-curr { fill: none; stroke: var(--v-accent-base, #1976d2); stroke-width: 2px; }
.spark-area-prev { fill: #9e9e9e; opacity: 0.08; }
.spark-line-prev { fill: none; stroke: #9e9e9e; stroke-width: 1.5px; stroke-dasharray: 4 3; }
.spark-hover-line { stroke: #bdbdbd; stroke-width: 1px; stroke-dasharray: 3 2; }
.spark-tooltip {
  position: absolute;
  top: 4px;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  font-size: 11px;
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}
.spark-tooltip-date { font-weight: 600; }
.spark-tooltip-prev { opacity: 0.7; }
</style>